<template>
    <div class="large-text-editor" :class="{ 'wm-toastui-dark': isDark }">
        <div class="text-h6 d-flex justify-space-between align-center">
            <span>{{ label }}</span>
            <span class="text-caption font-weight-regular" :class="{ 'error--text': isOverLimit }">
                {{ charCount.toLocaleString() }} / {{ maxLength.toLocaleString() }}
            </span>
        </div>
        <editor
            ref="toastEditor"
            initial-edit-type="wysiwyg"
            preview-style="tab"
            :initial-value="value || ''"
            :height="editorHeight"
            :options="{ minHeight: '200px', language: 'en-GB' }"
            @load="handleLoad"
            @change="handleChange"
        />
        <!-- Carries the length limit into the surrounding v-form so an over-length body blocks
             Save, and surfaces the error message. Renders no control of its own. -->
        <v-input :value="value" :rules="lengthRules" hide-details="auto" class="pt-1 mt-0" />
    </div>
</template>
<script>
import 'codemirror/lib/codemirror.css'
import '@toast-ui/editor/dist/toastui-editor.css'
import '@/assets/toastui-editor-dark.css'
import { Editor } from '@toast-ui/vue-editor'

export default {
    components: { Editor },
    computed: {
        isDark() {
            return this.$vuetify.theme.dark
        },
        charCount() {
            return (this.value || '').length
        },
        isOverLimit() {
            return this.charCount > this.maxLength
        },
        lengthRules() {
            // Counts the stored markdown (what goes to the DB), not the rendered text.
            return [() => !this.isOverLimit || `Maximum ${this.maxLength.toLocaleString()} characters`]
        },
    },
    props: {
        value: {
            type: String,
            // Not required + default '': a new entity's description arrives as null, and Vue only
            // skips the String type-check for a null value when the prop is optional. This clears
            // the "Invalid prop: Expected String, got Null" warning on new forms.
            default: '',
        },
        label: {
            type: String,
            required: true,
        },
        editorHeight: {
            type: String,
            default: '500px',
        },
        // System-wide character ceiling for every rich-text field. The backing columns are
        // MEDIUMTEXT (16 MB), so this is a UX guardrail well below any posting/retrieval limit,
        // not a storage constraint. Overridable per field if one ever needs a tighter cap.
        maxLength: {
            type: Number,
            default: 100000,
        },
    },
    created() {
        // Toast UI re-serialises markdown when it loads (normalises CRLF -> LF, escapes literal
        // markup, wraps HTML), so getMarkdown() !== value even when the user has changed nothing.
        // The baseline (set on @load) is the editor's own normalised rendering of the current
        // value; changes are only emitted once they diverge from it, so opening a form no longer
        // marks it dirty. Until load, _editorReady is false and every change is ignored.
        //
        // These flags are set here, not in mounted(): the child <editor> mounts (and can fire its
        // synchronous @load) before this component's mounted() hook runs, so initialising them
        // later would clobber a baseline that handleLoad already captured.
        this._editorReady = false
        this._normalizedValue = this.value || ''
        // Guards handleChange while we push a value in programmatically (reload/reset), so the
        // resulting change event isn't mistaken for a user edit.
        this._suppressChange = false
    },
    mounted() {
        // Toast UI v2 doesn't populate CodeMirror on mode switch from WYSIWYG.
        // Force-sync content then refresh so CodeMirror activates its scrollbar
        // and correctly computes cursor/selection positions.
        this._onChangeMode = mode => {
            if (mode === 'markdown') {
                this.$nextTick(() => {
                    const md = this.$refs.toastEditor.invoke('getMarkdown')
                    this.$refs.toastEditor.invoke('setMarkdown', md)
                    this.$refs.toastEditor.invoke('getCodeMirror').refresh()
                })
            }
        }
        this.$refs.toastEditor.invoke('on', 'changeMode', this._onChangeMode)
    },
    beforeDestroy() {
        this.$refs.toastEditor.invoke('off', 'changeMode', this._onChangeMode)
    },
    watch: {
        value(newValue) {
            if (!this.$refs.toastEditor) return
            const current = this.$refs.toastEditor.invoke('getMarkdown')
            if (newValue !== current) {
                // Programmatic value change (doc reload / reset), not a user edit. Suppress the
                // change it triggers and re-baseline to the editor's normalised rendering.
                this._suppressChange = true
                this.$refs.toastEditor.invoke('setMarkdown', newValue || '')
                this._normalizedValue = this.$refs.toastEditor.invoke('getMarkdown')
                this._suppressChange = false
            }
        },
    },
    methods: {
        handleLoad(editorInstance) {
            // Use the instance handed to the load event: @load can fire while the child is still
            // mounting, before this.$refs.toastEditor is wired up. Fall back to the ref for safety.
            const editor = editorInstance || this.$refs.toastEditor
            this._normalizedValue = editor.getMarkdown ? editor.getMarkdown() : editor.invoke('getMarkdown')
            this._editorReady = true
        },
        handleChange() {
            if (!this._editorReady || this._suppressChange) return
            const markdown = this.$refs.toastEditor.invoke('getMarkdown')
            // Compare against the editor's own normalised baseline, not the raw prop: the init
            // round-trip and mode switches change formatting without changing meaning.
            if (markdown === this._normalizedValue) return
            this._normalizedValue = markdown
            this.$emit('input', markdown)
        },
    },
}
</script>
